import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EXTEND_SECONDS,
  STALE_AFTER_MS,
  createRestStore,
  extendRest,
  remainingSeconds,
  skipRest,
  startRest,
} from "./restTimer";

// A short double buzz when the rest is over (ignored where unsupported,
// e.g. iOS Safari — the screen says it too).
export const VIBRATE_PATTERN = [200, 100, 200];

const isVisible = () =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

const canVibrate = () =>
  typeof navigator !== "undefined" && typeof navigator.vibrate === "function";

function quietly(fn) {
  try {
    Promise.resolve(fn()).catch(() => {});
  } catch {
    // a browser that throws here has nothing for us to undo
  }
}

/**
 * The D11 rest timer for one person (`userId`; null keeps it in memory),
 * seen from one session's page (`sessionId`).
 *
 * The rest is a stored end time (restTimer.js), so the time left is
 * recomputed — every second while it runs, aligned to the rest's own
 * seconds, and at once when the page becomes visible again — never
 * counted down. While it runs it holds a screen Wake Lock where the
 * browser has one (released on stop and unmount, taken again when the page
 * comes back: the browser drops it on hide). When the time is up it
 * vibrates once, where supported.
 *
 * A rest that ended over STALE_AFTER_MS ago is history, as restTimer's
 * read() says: found on a wake (a phone locked for hours), it's dropped
 * without a buzz or "Rest over". A stored rest from another session (`own`
 * false) is left alone: no ticks, no wake lock, no buzz.
 *
 * @returns {{
 *   rest: {endsAt: number, seconds: number, sessionId?: any, exercise?: string} | null,
 *   own: boolean,
 *   remaining: number, total: number, running: boolean, ended: boolean,
 *   wakeLockSupported: boolean,
 *   start: (opts: {seconds: number, sessionId?: any, exercise?: string}) => void,
 *   extend: (by?: number) => void,
 *   skip: () => void,
 * }}
 */
export default function useRestTimer(userId, { sessionId } = {}) {
  const store = useMemo(
    () => (userId === null || userId === undefined || userId === "" ? null : createRestStore(userId)),
    [userId],
  );
  const [rest, setRest] = useState(() => store?.read() ?? null);
  const [now, setNow] = useState(() => Date.now());
  // Render mirror for the handlers, so +30 s twice in a row adds 60.
  const restRef = useRef(rest);
  restRef.current = rest;
  // The rest whose end has been signalled; one that had already ended
  // before this page saw it doesn't buzz.
  const signalled = useRef(rest && rest.endsAt <= now ? rest.endsAt : null);

  // Signed in as someone else: their rest, not the last person's.
  const storeRef = useRef(store);
  useEffect(() => {
    if (storeRef.current === store) return;
    storeRef.current = store;
    const t = Date.now();
    const next = store?.read(t) ?? null;
    signalled.current = next && next.endsAt <= t ? next.endsAt : null;
    setRest(next);
    setNow(t);
  }, [store]);

  const endsAt = rest?.endsAt ?? null;
  const running = endsAt !== null && endsAt > now;
  // This page's rest (a rest stored without a session is anyone's).
  const own =
    rest !== null && (sessionId === undefined || rest.sessionId === undefined || rest.sessionId === sessionId);
  const ticking = running && own;

  // Read the clock; ring once if this reading is past the end. A side
  // effect from the computed value, never from inside a setState updater.
  const observe = useCallback(() => {
    const t = Date.now();
    setNow(t);
    if (endsAt === null) return;
    if (t - endsAt > STALE_AFTER_MS) {
      // Ended long ago (the phone slept through it): history, not "next set".
      signalled.current = endsAt;
      restRef.current = null;
      store?.clear();
      setRest(null);
      return;
    }
    if (own && t >= endsAt && signalled.current !== endsAt) {
      signalled.current = endsAt;
      if (canVibrate()) quietly(() => navigator.vibrate(VIBRATE_PATTERN));
    }
  }, [endsAt, own, store]);

  // Tick each second on the rest's own second boundaries, so "0:00" and
  // the buzz land on the end time, not up to a second late.
  useEffect(() => {
    if (!ticking) return undefined;
    let interval;
    const offset = (endsAt - Date.now()) % 1000;
    const first = setTimeout(
      () => {
        observe();
        interval = setInterval(observe, 1000);
      },
      offset > 0 ? offset : 1000,
    );
    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, [ticking, endsAt, observe]);

  // Back from a locked screen or another tab: the right time at once.
  useEffect(() => {
    document.addEventListener("visibilitychange", observe);
    return () => document.removeEventListener("visibilitychange", observe);
  }, [observe]);

  const wakeLockSupported =
    typeof navigator !== "undefined" && typeof navigator.wakeLock?.request === "function";

  useEffect(() => {
    if (!ticking || !wakeLockSupported) return undefined;
    let sentinel = null;
    let requesting = false;
    let stopped = false;
    const release = () => {
      const lock = sentinel;
      sentinel = null;
      if (lock && !lock.released) quietly(() => lock.release());
    };
    const acquire = async () => {
      if (stopped || sentinel || requesting || !isVisible()) return;
      requesting = true;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (stopped) {
          quietly(() => lock.release());
          return;
        }
        sentinel = lock;
        lock.addEventListener?.("release", () => {
          if (sentinel === lock) sentinel = null;
        });
      } catch {
        // Refused (battery saver, a permissions policy): the screen may
        // sleep, and the stored end time still tells the truth on wake.
      } finally {
        requesting = false;
      }
    };
    const onVisibility = () => {
      if (isVisible()) acquire();
      else release(); // the browser drops it on hide anyway
    };
    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [ticking, wakeLockSupported]);

  const start = useCallback(
    ({ seconds, sessionId, exercise } = {}) => {
      if (!(seconds > 0)) return;
      const t = Date.now();
      setRest(startRest(store, { seconds, sessionId, exercise }, t));
      setNow(t);
    },
    [store],
  );

  const extend = useCallback(
    (by = EXTEND_SECONDS) => {
      const t = Date.now();
      const next = extendRest(store, restRef.current, by, t);
      if (!next) return;
      restRef.current = next;
      setRest(next);
      setNow(t);
    },
    [store],
  );

  const skip = useCallback(() => {
    restRef.current = skipRest(store);
    setRest(null);
  }, [store]);

  const remaining = remainingSeconds(rest, now);
  return {
    rest,
    own,
    remaining,
    total: rest?.seconds ?? 0,
    running,
    ended: rest !== null && !running,
    wakeLockSupported,
    start,
    extend,
    skip,
  };
}
